import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './components/Header';
import BookTypeSelector from './components/BookTypeSelector';
import FictionForm from './components/FictionForm';
import NonfictionForm from './components/NonfictionForm';
import DescriptionOutput from './components/DescriptionOutput';
import Tips from './components/Tips';
import './App.css';

function App() {
  const [bookType, setBookType] = useState('');
  const [bookData, setBookData] = useState({});
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [currentStep, setCurrentStep] = useState('type');

  const handleBookTypeSelect = (type) => {
    setBookType(type);
    setCurrentStep('form');
  };

  const handleFormSubmit = (data) => {
    setBookData(data);
    generateDescription(data, bookType);
    setCurrentStep('output');
  };

  const generateDescription = (data, type) => {
    let description = '';
    
    if (type === 'fiction') {
      description = generateFictionDescription(data);
    } else {
      description = generateNonfictionDescription(data);
    }
    
    setGeneratedDescription(description);
  };

  const generateFictionDescription = (data) => {
    const hooks = [
      `When ${data.protagonist} ${data.incitingIncident}, their faith is put to the ultimate test.`,
      `${data.protagonist} never expected that ${data.incitingIncident} would change everything.`,
      `In a world where ${data.setting}, ${data.protagonist} must choose between ${data.conflict}.`,
      `${data.protagonist}'s life takes an unexpected turn when ${data.incitingIncident}.`
    ];

    const hook = hooks[Math.floor(Math.random() * hooks.length)];
    
    return `${hook}

${data.premise}

${data.stakes ? `With ${data.stakes} hanging in the balance, ` : ''}${data.protagonist} must navigate ${data.conflict} while discovering that God's plan is greater than they ever imagined. 

${data.themes ? `This powerful story explores themes of ${data.themes}, ` : ''}reminding us that even in our darkest moments, faith can light the way forward.

${data.genre === 'romance' ? 'A heartwarming Christian romance' : 
  data.genre === 'thriller' ? 'A gripping Christian thriller' :
  data.genre === 'historical' ? 'An inspiring historical Christian fiction' :
  data.genre === 'contemporary' ? 'A moving contemporary Christian story' :
  'An uplifting Christian novel'} that will keep you turning pages late into the night.

Perfect for readers who love ${data.comparableAuthors || 'inspirational fiction'} and stories that celebrate the transformative power of faith.`;
  };

  const generateNonfictionDescription = (data) => {
    const openings = [
      `Are you struggling with ${data.problem}? You're not alone.`,
      `What if the key to ${data.transformation} was simpler than you thought?`,
      `In a world that often feels overwhelming, finding ${data.solution} can seem impossible.`,
      `Every Christian faces moments when ${data.problem} feels insurmountable.`
    ];

    const opening = openings[Math.floor(Math.random() * openings.length)];
    
    return `${opening}

${data.premise}

In "${data.title || 'this transformative book'}," ${data.author || 'the author'} ${data.credentials ? `(${data.credentials}) ` : ''}shares ${data.approach} to help you ${data.transformation}.

${data.keyPoints ? `You'll discover:
${data.keyPoints.split(',').map(point => `• ${point.trim()}`).join('\n')}` : ''}

${data.biblicalFoundation ? `Grounded in biblical truth and ` : ''}${data.practicalApplication ? 'filled with practical applications' : 'packed with real-world wisdom'}, this book offers hope and healing for ${data.targetAudience || 'anyone seeking transformation'}.

${data.personalStory ? 'Through personal stories and biblical insights, ' : ''}${data.author || 'The author'} shows you how to ${data.outcome} and experience the abundant life God has planned for you.

${data.category === 'devotional' ? 'A daily devotional' :
  data.category === 'study' ? 'A comprehensive Bible study' :
  data.category === 'memoir' ? 'An inspiring memoir' :
  data.category === 'self-help' ? 'A practical guide' :
  'An essential resource'} for ${data.targetAudience || 'every Christian'} ready to ${data.transformation}.`;
  };

  const resetApp = () => {
    setBookType('');
    setBookData({});
    setGeneratedDescription('');
    setCurrentStep('type');
  };

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {currentStep === 'type' && (
              <BookTypeSelector onSelect={handleBookTypeSelect} />
            )}
            
            {currentStep === 'form' && bookType === 'fiction' && (
              <FictionForm onSubmit={handleFormSubmit} onBack={resetApp} />
            )}
            
            {currentStep === 'form' && bookType === 'nonfiction' && (
              <NonfictionForm onSubmit={handleFormSubmit} onBack={resetApp} />
            )}
            
            {currentStep === 'output' && (
              <DescriptionOutput 
                description={generatedDescription}
                bookType={bookType}
                onReset={resetApp}
                onRegenerate={() => generateDescription(bookData, bookType)}
              />
            )}
          </motion.div>
          
          <Tips />
        </main>
      </div>
    </Router>
  );
}

export default App;